import React, { useEffect, useState, useMemo } from "react";

import { Col, Input, Row, Button, CarouselItem } from "reactstrap";
import RiskAssessmentStep from "./RiskAssessmentStep";
import DataService from "infrastructure/Services/DataService";
import Step1 from "./Step1";
import Step2 from "./Step2";
import "../Form.scss";
import FormColumn from "../../../../components/Common/FormColumn";
import Submit from "../Submit";
import AlertModal from "components/Common/AlertModal";
import ApiService from "infrastructure/Services/ApiService";
import moment from "moment";
import FormStepHeader from "../../../../components/Common/FormStepHeader";
import RiskMatrix from "components/Common/RiskMatrix";

export default ({ booking, onCloseButtonPress }) => {
  const [isValid, setIsValid] = useState(false);
  const startValidatingOn = new Date(2025, 12, 8, 0, 0, 0);
  const [error, setError] = useState(false);
  const baseForm = { campusId: null };
  const [newBooking, setNewBooking] = useState({ ...booking, ...baseForm });
  const [step, setStep] = useState(1);
  const [riskTemplates, setRiskTemplates] = useState(null);
  const [chemicals, setChemicals] = useState([]);
  const [chemicalSearch, setChemicalSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showRiskWarningModal, setShowRiskWarningModal] = useState(false);
  const [loadedFromSavedPractical, setLoadedFromSavedPractical] = useState(false);
  const [risksModified, setRisksModified] = useState(false);

  const getChemicalSortName = (item) => {
    return String(item?.documentName || item?.DocumentName || item?.path || item?.Path || item?.partNumber || item?.PartNumber || "").trim();
  };

  const sortedChemicals = useMemo(() => {
    return [...(chemicals || [])].sort((left, right) => {
      const leftName = getChemicalSortName(left).toLocaleLowerCase();
      const rightName = getChemicalSortName(right).toLocaleLowerCase();
      return leftName.localeCompare(rightName);
    });
  }, [chemicals]);

  const filteredChemicals = useMemo(() => {
    const query = chemicalSearch.trim().toLowerCase();
    if (!query) {
      return sortedChemicals;
    }

    return sortedChemicals.filter((item) => {
      const searchableText = [
        item?.documentName,
        item?.DocumentName,
        item?.path,
        item?.Path,
        item?.vendor,
        item?.Vendor,
        item?.partNumber,
        item?.PartNumber,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [chemicalSearch, sortedChemicals]);

  const getData = async () => {
    const riskData = await DataService.labBookings.riskTemplate();
    try {
      const chemicalData = await ApiService.get("chemicals");
      const normalizedChemicals = Array.isArray(chemicalData) ? chemicalData : [];
      setChemicals(normalizedChemicals.sort((left, right) => {
        const leftName = String(left?.documentName || left?.DocumentName || left?.path || left?.Path || left?.partNumber || left?.PartNumber || "").trim().toLocaleLowerCase();
        const rightName = String(right?.documentName || right?.DocumentName || right?.path || right?.Path || right?.partNumber || right?.PartNumber || "").trim().toLocaleLowerCase();
        return leftName.localeCompare(rightName);
      }));
    } catch (error) {
      setChemicals([]);
    }

    setRiskTemplates(riskData);
  };

  const isControlMeasureSelected = (id) => {

    if (!newBooking.controlMeasures) return false;
    const cm = newBooking.controlMeasures.find(x => x.id === id);
    return cm != null;
  };

  const isMitigationSelected = (controlMeasureId, mitigation) => {
    if (!newBooking.controlMeasures) return false;
    const cm = newBooking.controlMeasures.find(x => x.id === controlMeasureId);
    if (!cm || !cm.mitigations) return false;
    return cm.mitigations.includes(mitigation);
  };

  const toggleMitigationOption = (controlMeasureId, mitigation) => {
    setRisksModified(true);
    setNewBooking((prevState) => {
      const newState = { ...prevState };
      if (!newState.controlMeasures) return newState;
      const cmIndex = newState.controlMeasures.findIndex(x => x.id === controlMeasureId);
      if (cmIndex === -1) return newState;

      // Create new array and new object to ensure React detects the change
      newState.controlMeasures = [...newState.controlMeasures];
      const cm = { ...newState.controlMeasures[cmIndex] };

      if (!cm.mitigations) {
        cm.mitigations = [mitigation];
      } else {
        const mitigationIndex = cm.mitigations.indexOf(mitigation);
        if (mitigationIndex > -1) {
          cm.mitigations = cm.mitigations.filter((_, i) => i !== mitigationIndex);
        } else {
          cm.mitigations = [...cm.mitigations, mitigation];
        }
      }

      newState.controlMeasures[cmIndex] = cm;

      return newState;
    });
  };

  const toggleControlMeasure = (id, name) => {
    setRisksModified(true);
    setNewBooking((prevState) => {
      const newState = { ...prevState };
      if (!newState.controlMeasures) {
        newState.controlMeasures = [{ id, name, value: true }];
        return newState;
      }
      const cmIndex = newState.controlMeasures.findIndex(x => x.id === id);
      if (cmIndex === -1) {
        newState.controlMeasures = [...newState.controlMeasures, { id, name, value: true }];
      }
      else {
        newState.controlMeasures = newState.controlMeasures.filter((_, i) => i !== cmIndex);
      }
      return newState;
    });
  };
  const getChemicalKey = (item) => item?.id || item?.Id || item?.documentName || item?.DocumentName || item?.path || item?.Path || item?.partNumber || item?.PartNumber;

  const isChemicalSelected = (item) => {
    const key = getChemicalKey(item);
    return (newBooking.chemicals || []).some((chemical) => getChemicalKey(chemical) === key);
  };

  const toggleChemicalSelection = (item) => {
    const key = getChemicalKey(item);
    setNewBooking((prevState) => {
      const existing = (prevState.chemicals || []).filter((chemical) => getChemicalKey(chemical) !== key);
      const next = isChemicalSelected(item)
        ? existing
        : [...existing, item];
      return { ...prevState, chemicals: next };
    });
  };

  const updateControlMeasureNotes = (controlMeasureId, notes) => {
    setRisksModified(true);
    setNewBooking((prevState) => {
      const newState = { ...prevState };
      if (!newState.controlMeasures) return newState;
      const cmIndex = newState.controlMeasures.findIndex(x => x.id === controlMeasureId);
      if (cmIndex === -1) return newState;
      newState.controlMeasures = newState.controlMeasures.map((cm, idx) =>
        idx === cmIndex ? { ...cm, mitigationOther: notes } : cm
      );
      return newState;
    });
  };

  const getControlMeasureNotes = (controlMeasureId) => {
    if (!newBooking.controlMeasures) return '';
    const cm = newBooking.controlMeasures.find(x => x.id === controlMeasureId);
    if (!cm || !cm.mitigationOther) return '';
    return cm.mitigationOther;
  };

  const copy = () => {
    var d = new moment(booking.bookingDate).format('yyyy-MM-DD');
    ApiService.post(`bookings/lab/${d}/${booking.id}/copy`)
      .then(() => {
        setShowModal(false);
        onCloseButtonPress(true);
      });
  }

  const updateValues = (args) => {
    setNewBooking((prevState) => {
      const newState = { ...prevState, ...args };
      return newState;
    });
  };

  const updateRiskValue = (item, args) => {
    setRisksModified(true);
    setNewBooking((prevState) => {
      const newState = { ...prevState };
      if (!newBooking.risks) {
        newState.risks = [args];
        return newState;
      }

      const index = newState.risks.findIndex((x) => x.id === item.id);

      if (index > -1) {
        newState.risks.splice(index, 1);
      }
      newState.risks.push(args);
      return newState;
    });
  };

  const next = () => {
    setStep((prevState) => {
      if (prevState === 3) {
        if (newBooking.controlMeasures == null || newBooking.controlMeasures.length === 0) {
          alert("Please complete control measures to complete your booking.");
          return prevState;
        }
        if (!newBooking.risks || newBooking.risks.length === 0) {
          const confirm = window.confirm("You have not added any risk assessments. Are you sure you want to proceed?");
          if (!confirm) {
            return prevState;
          }
        }
        // Check if loaded from saved practical and risks not modified
        if (loadedFromSavedPractical && !risksModified) {
          setShowRiskWarningModal(true);
          return prevState;
        }
      }
      return prevState + 1;
    });
  };
  const prev = () => {
    setStep((prevState) => {
      if (prevState === 1) return prevState;
      return prevState - 1;
    });
  };

  const deleteBooking = () => {
    var d = new moment(booking.bookingDate).format('yyyy-MM-DD');
    ApiService.delete(`booking/lab/${d}/${booking.id}`)
      .then(() => {
        setShowModal(false);
        onCloseButtonPress(true);
      });
  }

  const proceedWithUnchangedRisks = () => {
    setShowRiskWarningModal(false);
    setStep((prevState) => prevState + 1);
  };

  const renderControlMethods = () => {
    return riskTemplates?.controlMeasures?.map((method) => (
      <React.Fragment key={method.id}><Col xs={4} key={method.name}>
        <div className="custom-control custom-checkbox mb-3">
          <input
            className="custom-control-input"
            id={`cbx_${method.id}`}
            type="checkbox"
            checked={isControlMeasureSelected(method.id)}
            onChange={e => toggleControlMeasure(method.id, method.name)}
          />
          <label className="custom-control-label" htmlFor={`cbx_${method.id}`}>
            {method.name}

          </label>
        </div></Col>
        <Col xs={8} key={`${method.name}-options`}>
          {isControlMeasureSelected(method.id) === true && (
            <div className="ml-3">
              {method.options && method.options.map((option, index) => (
                <div className="custom-control custom-checkbox mb-2" key={`control-measure-${method.name}-option-${index}`}>
                  <input
                    className="custom-control-input"
                    id={`cbx_${method.id}_${index}`}
                    type="checkbox"
                    checked={isMitigationSelected(method.id, option)}
                    onChange={() => toggleMitigationOption(method.id, option)}
                  />
                  <label className="custom-control-label" htmlFor={`cbx_${method.id}_${index}`}>
                    {option}
                  </label>
                </div>
              ))}
              <div className="custom-control custom-checkbox mb-2">
                <input
                  className="custom-control-input"
                  id={`cbx_${method.id}_other`}
                  type="checkbox"
                  checked={isMitigationSelected(method.id, 'Other')}
                  onChange={() => toggleMitigationOption(method.id, 'Other')}
                />
                <label className="custom-control-label" htmlFor={`cbx_${method.id}_other`}>
                  Other
                </label>
              </div>
              {isMitigationSelected(method.id, 'Other') && (
                <Input type="text" bsSize="sm" className="mt-2"
                  placeholder="Please specify..."
                  value={getControlMeasureNotes(method.id) || ''}
                  onChange={e => updateControlMeasureNotes(method.id, e.target.value)}
                />
              )}
            </div>
          )}
        </Col></React.Fragment>
    ))
  };


  useEffect(() => {
    getData();
  }, []);

  const renderRiskSteps = () => {
    let className = `step`;

    if (step < 3) {
      className += ` next`;
    } else if (step > 3) {
      className += ` prev`;
    } else {
      className += ` current`;
    }
    return <div className={className}>
      <FormStepHeader
        sectionName={`Risk Assessment`}
        title={`Risks`}
        icon={`risk`}
      />
      <div className="customScroll pr-2" style={{ maxHeight: 500, overflowX: `hidden`, overflowY: `scroll` }}>
        <Row className="mt-3 mb-4">
          <Col xs={12}>
            <h3>Chemicals in use</h3>
            <p className="text-muted mb-3">Select any chemicals relevant to this booking.</p>
            <Input
              type="text"
              bsSize="sm"
              className="mb-2"
              placeholder="Search chemicals..."
              value={chemicalSearch}
              onChange={(e) => setChemicalSearch(e.target.value)}
              onKeyUp={(e) => setChemicalSearch(e.target.value)}
            />
            <div className="border rounded p-2" style={{ maxHeight: 320, overflowY: "auto" }}>
              {(filteredChemicals || []).map((item) => (
                <div className="custom-control custom-checkbox mb-2" key={getChemicalKey(item)}>
                  <input
                    className="custom-control-input"
                    id={`cbx_chemical_${getChemicalKey(item)}`}
                    type="checkbox"
                    checked={isChemicalSelected(item)}
                    onChange={() => toggleChemicalSelection(item)}
                  />
                  <label className="custom-control-label" htmlFor={`cbx_chemical_${getChemicalKey(item)}`}>
                    {item.documentName || item.DocumentName || item.path || item.Path || "Chemical"}
                    {item.vendor || item.Vendor ? ` — ${item.vendor || item.Vendor}` : ""}
                  </label>
                </div>
              ))}
              {(!filteredChemicals || filteredChemicals.length === 0) && (
                <p className="text-muted mb-0">
                  {chemicalSearch ? "No chemicals match your search." : "No chemicals are currently available."}
                </p>
              )}
            </div>
          </Col>
        </Row>
        <Row className="mb-5
        "><Col xs={12} >
            <h3>Risk Matrix</h3>
            <RiskMatrix />
          </Col></Row>
        <Row>
          <Col xs={12} >
            {(riskTemplates ? riskTemplates.risks : []).map((item, index) => (
              <RiskAssessmentStep
                key={item.id}
                riskTemplate={item}
                booking={newBooking}
                onUpdateValue={updateRiskValue}
              />
            ))}
          </Col>
        </Row>
        <Row>
          <Col className={`my-1`}>
            <h3>Control Measures Required</h3>
          </Col>
        </Row>
        <Row>{renderControlMethods()}</Row>
        <Row>
          <FormColumn label={`Other`} columns={1}>
            <Input type="textarea" name="Notes" id="Notes" rows={3} value={newBooking.notes} onChange={e => updateValues({ notes: e.target.value })} />
          </FormColumn>
        </Row>
      </div>
    </div>;
  }

  return (
    <>
      <Row>
        <Col>
          <div className={`step-container custom-scroll`}>
            <i
              className={`fa fa-times float-right cursor-pointer modal-close-button`}
              onClick={onCloseButtonPress}
            ></i>
            <Step1
              currentStep={step}
              index={1}
              booking={newBooking}
              onUpdateValues={updateValues}
              setLoadedFromSavedPractical={setLoadedFromSavedPractical}
            />
            <Step2
              currentStep={step}
              index={2}
              booking={newBooking}
              onUpdateValues={updateValues}
            />


            {renderRiskSteps()}

            <Submit
              error={error}
              setError={setError}
              currentStep={step}
              index={4}
              booking={newBooking}
              onUpdateValues={updateValues}
              onBookingSaved={() => onCloseButtonPress(true)}
            />
          </div>
        </Col>
      </Row>
      {(step < 4 || error) && <Row>
        <Col>
          {step > 1 && (
            <Button
              className="ml-auto"
              color="link"
              data-dismiss="modal"
              type="button"
              onClick={prev}
            >
              <i className={`fa fa-chevron-left`} /> Back
            </Button>
          )}
        </Col>
        <Col>
          {(step < 4) && <Button
            color="primary"
            type="button"
            className={`float-right`}
            onClick={next}
            disabled={!newBooking || !newBooking.yearLevel || !newBooking.room || !newBooking.practicalName || !newBooking.class || (!newBooking.setsRequired || newBooking.setsRequired < 1) || (step === 3 && !newBooking.practicalId && new Date() > startValidatingOn && newBooking.risks?.some(risk => risk.controlMeasures?.some(cm => cm.value === true && (!cm.mitigations || cm.mitigations.length === 0 || (cm.mitigations.includes('Other') && (!cm.mitigationOther || cm.mitigationOther.trim() === ''))))))}
          >
            {step === 3 ? `Submit` : `Next`} <i className={`fa fa-chevron-right`} />
          </Button>}
          {booking.id && <><Button
            color="danger"
            type="button"
            className={`float-right mr-2`}
            onClick={() => setShowModal(true)}
          >
            <i className={`fa fa-trash`} /> &nbsp; Delete
          </Button>
            <Button
              color="warning"
              type="button"
              className={`float-right mr-2`}
              onClick={copy}
            >
              <i className={`fa fa-copy`} /> &nbsp; Copy
            </Button></>}
        </Col>
      </Row>}
      <AlertModal title={`Are you sure?`} content={`Are you sure you want to delete this booking?`} show={showModal} onConfirm={deleteBooking} onCancel={() => setShowModal(false)} />
      <AlertModal
        title={`Risk Assessment Warning`}
        content={`You have not updated the risk assessment for this booking. Risks may alter for different classrooms, staff and/or students. Are you sure you want to proceed?`}
        show={showRiskWarningModal}
        onConfirm={proceedWithUnchangedRisks}
        onCancel={() => setShowRiskWarningModal(false)}
        confirmText={`Proceed`}
      />
    </>
  );
};
